import { ApiTarget, HttpClient } from '@packages/core/http';
import {
    ChangeConfidenceResponse,
    CidCvnEligibilityResponse,
    CidCvnListResponse,
    CidCvnOcrResponse,
    CidCvnReferenceLastUpdatedResponse,
    CidCvnReportResponse,
    CidCvnValidateImageResponse,
} from '@packages/models/api/cid-cvn';

export class CidCvnService {
    constructor(private http: HttpClient) {}

    getCidCvnEligibility({
        vin,
        ignoreCache = true,
        signal,
    }: {
        vin: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<CidCvnEligibilityResponse>({
            method: 'GET',
            url: `/cidcvn/${vin}/eligible`,
            ignoreCache,
            signal,
        });
    }

    getReferenceDataLastUpdated({ ignoreCache = true, signal }: { ignoreCache?: boolean; signal?: AbortSignal } = {}) {
        return this.http.orchestrateRequest<CidCvnReferenceLastUpdatedResponse>({
            method: 'GET',
            url: '/cidcvn/reference/last-updated',
            ignoreCache,
            signal,
        });
    }

    scanImageFile(data: FormData, vin: string, modelYear?: number) {
        return this.http.orchestrateRequest<CidCvnOcrResponse>({
            method: 'POST',
            url: `/extract-and-suggest?vin=${vin}&modelYear=${modelYear ?? ''}`,
            body: data,
            apiTarget: ApiTarget.OcrService,
        });
    }

    getChangeConfidence(body: {
        extracted_cvn?: string;
        changed_cvn?: string;
        extracted_cin?: string;
        changed_cin?: string;
    }) {
        return this.http.orchestrateRequest<ChangeConfidenceResponse>({
            method: 'POST',
            url: `/change-confidence`,
            body,
            apiTarget: ApiTarget.OcrService,
        });
    }

    validateRecord(
        vin: string,
        cvn: string,
        cid: string,
        cpoAnswer: boolean,
        retailerId: string,
        file: FormData,
        originalValues?: { originalCid?: string; originalCvn?: string }
    ) {
        const isCidChanged = originalValues?.originalCid && originalValues.originalCid !== cid;
        const isCvnChanged = originalValues?.originalCvn && originalValues.originalCvn !== cvn;
        const isCidCvnManuallyUpdated = !!(isCidChanged || isCvnChanged);

        const queryParams = new URLSearchParams({
            cid,
            cvn,
            cpoInd: String(cpoAnswer),
            retailerId,
            isCidCvnManuallyUpdated: String(isCidCvnManuallyUpdated),
        });

        if (isCidCvnManuallyUpdated) {
            queryParams.append('originalCid', originalValues!.originalCid!);
            queryParams.append('originalCvn', originalValues!.originalCvn!);
        }

        return this.http.orchestrateRequest<CidCvnValidateImageResponse>({
            method: 'POST',
            url: `/cidcvn/${vin}/validate?${queryParams.toString()}`,
            body: file,
        });
    }

    getCidCvnList(vin: string, ignoreCache: boolean = true) {
        return this.http.orchestrateRequest<CidCvnListResponse>({
            method: 'GET',
            url: `/cidcvn/${vin}/history`,
            ignoreCache,
        });
    }

    getCidCvnReport(id: string, ignoreCache: boolean = true) {
        return this.http.orchestrateRequest<CidCvnReportResponse>({
            method: 'GET',
            url: `/cidcvn/status/${id}`,
            ignoreCache,
        });
    }

    setCidCvnCheckLockout(vin: string) {
        return this.http.orchestrateRequest<void>({
            method: 'POST',
            url: `/cidcvn/${vin}/lockout`,
        });
    }

    retryCidCvnCheck(id: string, ignoreCache: boolean = true) {
        return this.http.orchestrateRequest<CidCvnReportResponse>({
            method: 'POST',
            url: `/cidcvn/${id}/recheck`,
            ignoreCache,
        });
    }
}
